<template>
  <a-select
    v-model:value="innerValue"
    mode="multiple"
    :options="normalizedOptions"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :filter-option="filterOption"
    :max-tag-count="maxTagCount"
    :status="status"
    :loading="loading"
    :not-found-content="notFoundContent"
    class="ant-multi-select"
    @change="onChange"
    @search="onSearch"
    @blur="onBlur"
  >
    <slot />
  </a-select>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import type { SelectOption } from "./types";

interface Props {
  modelValue?: Array<string | number>;
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  loading?: boolean;
  maxTagCount?: number | "responsive";
  status?: "" | "error" | "warning";
  notFoundContent?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
  options: () => [],
  placeholder: "Chọn nhiều...",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  loading: false,
  maxTagCount: "responsive",
  status: "",
  notFoundContent: "Không có dữ liệu",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: Array<string | number>): void;
  (e: "change", value: Array<string | number>, options?: SelectOption[]): void;
  (e: "search", value: string): void;
  (e: "blur", event: FocusEvent): void;
}>();

const normalizedOptions = computed(() =>
  (props.options || []).map((opt) => ({
    label: opt.label,
    value: opt.value,
    disabled: opt.disabled,
  }))
);

const removeAccent = (str: string) =>
  str
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const filterOption = (input: string, option: any) => {
  const label = String(option?.label ?? "");
  return removeAccent(label).includes(removeAccent(input));
};

const innerValue = computed({
  get: () => props.modelValue ?? [],
  set: (val: Array<string | number>) => {
    emit("update:modelValue", val);
  },
});

const onChange = (val: Array<string | number>, options: any) => {
  emit("update:modelValue", val);
  emit("change", val, options);
};

const onSearch = (val: string) => emit("search", val);
const onBlur = (e: FocusEvent) => emit("blur", e);
</script>

<style scoped>
.ant-multi-select {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Multi Select
  Global: <h-multiSelect v-model="tags" :options="options" />
  Import: import { MultiSelect } from '@/components/ant/select'

  Props: modelValue (array), options, maxTagCount, showSearch, allowClear...
  Events: update:modelValue, change, search, blur
  Docs: src/components/ant/select/README.md
-->
